import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    Direction,
    Outside,
    Edge,
    positionKey,
    positionsEqual,
    isInside,
    edgeKey,
    isRimEdge,
    edgeAdjacentCells,
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

/** Every direction a start can take, in clockwise order (the rotation cycle). */
const CLOCKWISE_DIRECTIONS: Direction[] = ['N', 'E', 'S', 'W'];

export interface BlockPath {
    positions: Position[];
    digitsHit: number[];
    digitsCrossedTwice: number[];
    loopClosed: boolean;
    stuck: boolean;
    stuckPosition: Position | null;
    endDirection: Direction | null;
    teleportIndices?: number[];
    /** Edge keys of walls the block actually redirects off during this trace. */
    wallsHit: Set<string>;
}

export class WallsGame {
    protected grid: Grid;
    private walls: Map<string, Direction> = new Map();
    protected cachedPath: BlockPath | null = null;
    private startPosition: Position | null = null;
    private startDirection: Direction | null = null;
    /** Player-chosen start direction, pinned to the cell it was chosen on. */
    private startOverride: { pos: Position; dir: Direction } | null = null;

    protected get gs() { return this.grid.serializer; }

    constructor(grid: Grid) {
        this.grid = grid;
    }

    // The start is derived by getPath(); resolve it first so these can be read alone.
    getStartPosition(): Position | null {
        this.getPath();
        return this.startPosition;
    }

    getStartDirection(): Direction | null {
        this.getPath();
        return this.startDirection;
    }

    /**
     * Turn the start marker one quarter-turn clockwise, overriding the auto-selected
     * direction. Only acts on the cell the marker currently sits on; the override then
     * holds until the auto-selection moves the start to another cell (see getPath).
     * Returns whether the direction changed.
     */
    rotateStartDirection(cell: Position): boolean {
        const start = this.getStartPosition();
        const current = this.getStartDirection();
        if (!start || !current || !positionsEqual(start, cell)) return false;

        const next = CLOCKWISE_DIRECTIONS[
            (CLOCKWISE_DIRECTIONS.indexOf(current) + 1) % CLOCKWISE_DIRECTIONS.length
        ];
        this.startOverride = { pos: { ...start }, dir: next };
        this.cachedPath = null;
        return true;
    }

    /** Whether the shown start direction is the player's choice rather than the auto pick. */
    hasStartOverride(): boolean {
        this.getPath();
        return this.startOverride !== null;
    }

    // Get valid directions for a wall based on its orientation
    getValidDirections(edge: Edge): Direction[] {
        if (edge.orient === 'h') {
            return ['W', 'E'];
        } else {
            return ['N', 'S'];
        }
    }

    getGrid(): Grid {
        return this.grid;
    }

    getWalls(): Map<string, Direction> {
        return new Map(this.walls);
    }

    getWallAt(edge: Edge): Direction | null {
        return this.walls.get(edgeKey(edge)) ?? null;
    }

    isValidEdge(edge: Edge): boolean {
        // Valid edges are internal (not on the rim)
        return !isRimEdge(edge);
    }

    canPlaceWall(edge: Edge): boolean {
        return this.isValidEdge(edge);
    }

    // Get all walls that are invalid (touching another wall)
    getInvalidWalls(): Set<string> {
        const invalid = new Set<string>();

        for (const key of this.walls.keys()) {
            const edge = this.grid.deserializeEdge(key);
            if (!edge) continue;
            const adjacent = this.grid.getEdgesTouchingEdge(edge);

            for (const adj of adjacent) {
                if (this.walls.has(edgeKey(adj))) {
                    invalid.add(key);
                    break;
                }
            }
        }

        return invalid;
    }

    // Check if there are any invalid walls
    hasInvalidWalls(): boolean {
        return this.getInvalidWalls().size > 0;
    }

    setWall(edge: Edge, direction: Direction | null): boolean {
        const key = edgeKey(edge);

        if (direction === null) {
            this.walls.delete(key);
            this.onBoardChanged();
            return true;
        }

        if (!this.canPlaceWall(edge)) return false;

        this.walls.set(key, direction);
        this.onBoardChanged();
        return true;
    }

    cycleWall(edge: Edge): void {
        if (!this.isValidEdge(edge)) return;

        const key = edgeKey(edge);
        const current = this.walls.get(key);
        const validDirs = this.getValidDirections(edge);

        if (current === undefined) {
            // Always allow placement - invalid walls are tracked separately
            this.walls.set(key, validDirs[0]);
        } else if (current === validDirs[0]) {
            this.walls.set(key, validDirs[1]);
        } else {
            this.walls.delete(key);
        }
        this.onBoardChanged();
    }

    /**
     * Invalidate the traced path after the player edits the board, and retire a start
     * direction override that would now hide a solved loop.
     *
     * Only board edits do this, never a rotation: turning the ring on a solved board is
     * a deliberate act (poking at a finished puzzle), whereas reaching the solution with
     * a forgotten override still pinned would silently swallow the win.
     */
    protected onBoardChanged(): void {
        this.cachedPath = null;
        if (!this.startOverride) return;
        if (this.getInvalidWalls().size > 0) return;

        const best = this.findBestCandidate();
        if (best && this.isWinningPath(best.path)) {
            this.startOverride = null;
        }
    }

    reset(): void {
        this.walls.clear();
        this.startPosition = null;
        this.startDirection = null;
        this.startOverride = null;
        this.cachedPath = null;
    }

    /**
     * Jumpstart a solution: place a couple of arrow walls so the block travels
     * from digit 1 to digit 2. The block is launched along one axis through
     * digit 1, then turned at the row/column crossing cell toward digit 2.
     * Tries a few wall combinations and keeps the first whose traced path hits
     * digit 1 immediately followed by digit 2.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const d1 = this.grid.getDigitPosition(this.grid.minDigit);
        const d2 = this.grid.getDigitPosition(this.grid.minDigit + 1);
        if (!d1 || !d2) return false;

        // Prefer an attempt whose path is "clean" (no digit crossed twice, so the
        // status reads as plain progress); otherwise fall back to any that joins.
        let fallback: Array<{ cell: Position; edgeDir: Direction; wallDir: Direction }> | null = null;

        for (const attempt of this.buildAutoStartAttempts(d1, d2)) {
            if (!this.applyAttempt(attempt)) continue;
            const result = this.joinResult();
            if (!result.joins) continue;
            if (result.clean) return true;   // keep these walls as-is
            if (!fallback) fallback = attempt;
        }

        if (fallback) {
            this.applyAttempt(fallback);
            return true;
        }

        this.reset();
        return false;
    }

    /** Reset, then place a candidate attempt's walls. False if any wall is illegal. */
    private applyAttempt(
        attempt: Array<{ cell: Position; edgeDir: Direction; wallDir: Direction }>
    ): boolean {
        this.reset();
        for (const w of attempt) {
            const edge = this.grid.getEdgeFromPosition(w.cell, w.edgeDir);
            if (!this.setWall(edge, w.wallDir)) return false;
        }
        return true;
    }

    /**
     * Evaluate the currently placed walls for autoStart: whether the auto-derived
     * block path hits digit 1 immediately followed by digit 2, and whether the
     * path crosses no digit twice (a clean, warning-free in-progress state).
     */
    private joinResult(): { joins: boolean; clean: boolean } {
        if (this.hasInvalidWalls()) return { joins: false, clean: false };
        const path = this.getPath();
        if (!path) return { joins: false, clean: false };

        const a = this.grid.minDigit;
        const b = this.grid.minDigit + 1;
        const hits = path.digitsHit;
        let joins = false;
        for (let i = 0; i + 1 < hits.length; i++) {
            if (hits[i] === a && hits[i + 1] === b) { joins = true; break; }
        }
        return { joins, clean: path.digitsCrossedTwice.length === 0 };
    }

    /**
     * Candidate wall sets for autoStart, each an L-path: launch the block
     * through digit 1 along one axis, then turn at the row/column crossing cell
     * toward digit 2. Both turn corners — (r1,c2) and (r2,c1) — and both launch
     * edges of digit 1 are tried.
     *
     * Generation rules guarantee digits 1 and 2 never share a row or column
     * (so both legs are well-defined) and that no digit sits in a corner 2×2
     * (so at least one corner's turn edge is internal) — see docs/generation.md.
     */
    private buildAutoStartAttempts(
        d1: Position, d2: Position
    ): Array<Array<{ cell: Position; edgeDir: Direction; wallDir: Direction }>> {
        const size = this.grid.size;
        const attempts: Array<Array<{ cell: Position; edgeDir: Direction; wallDir: Direction }>> = [];

        // Horizontal launch edges of digit 1 (N/S edges carry W/E arrows).
        const hLaunch: Direction[] = [];
        if (d1.row >= 1) hLaunch.push('N');
        if (d1.row <= size - 2) hLaunch.push('S');
        // Vertical launch edges of digit 1 (E/W edges carry N/S arrows).
        const vLaunch: Direction[] = [];
        if (d1.col <= size - 2) vLaunch.push('E');
        if (d1.col >= 1) vLaunch.push('W');

        const opposite: Record<Direction, Direction> = { N: 'S', S: 'N', E: 'W', W: 'E' };
        const legH: Direction = d2.col > d1.col ? 'E' : 'W';
        const legV: Direction = d2.row > d1.row ? 'S' : 'N';

        // Horizontal first, turn at (r1, c2). launchDir runs the block along row
        // r1 to the corner (directly, or the wrap-around way); turnDir then
        // sends it down column c2 to digit 2 (directly or wrapping). The block
        // wraps the torus, so the "long way" can route around a blocking digit.
        const cornerH: Position = { row: d1.row, col: d2.col };
        for (const launchDir of [legH, opposite[legH]]) {
            for (const turnDir of [legV, opposite[legV]]) {
                for (const e of hLaunch) {
                    attempts.push([
                        { cell: d1, edgeDir: e, wallDir: launchDir },
                        { cell: cornerH, edgeDir: launchDir, wallDir: turnDir },
                    ]);
                }
            }
        }
        // Vertical first, turn at (r2, c1).
        const cornerV: Position = { row: d2.row, col: d1.col };
        for (const launchDir of [legV, opposite[legV]]) {
            for (const turnDir of [legH, opposite[legH]]) {
                for (const e of vLaunch) {
                    attempts.push([
                        { cell: d1, edgeDir: e, wallDir: launchDir },
                        { cell: cornerV, edgeDir: launchDir, wallDir: turnDir },
                    ]);
                }
            }
        }

        return attempts;
    }

    /**
     * Candidate starts: every digit cell, in each of the four directions.
     * Anchoring the start on a digit keeps it predictable — the player always sees
     * a marker on a cell they recognise, and it exists even before any wall is placed.
     */
    private getStartCandidates(): Array<{pos: Position, dir: Direction}> {
        const candidates: Array<{pos: Position, dir: Direction}> = [];

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.grid.getDigitPosition(digit);
            if (!pos) continue;
            for (const dir of CLOCKWISE_DIRECTIONS) {
                candidates.push({ pos, dir });
            }
        }

        return candidates;
    }

    /**
     * Length of the correct digit chain led by the start, in cyclic order (9 wraps to 1):
     * how many digits the block takes in sequence from the start before breaking order.
     *
     * Measured from the start rather than from the best run anywhere along the path, so
     * the marker always lands on the digit its chain begins with. A path that merely
     * wanders into a chain part-way earns nothing for it — the candidate that starts at
     * that chain's head scores it instead.
     */
    private getChainFromStart(path: BlockPath): number {
        const hits = path.digitsHit;
        if (hits.length === 0) return 0;

        let run = 1;
        for (let i = 1; i < hits.length; i++) {
            if (hits[i] !== this.grid.nextDigit(hits[i - 1])) break;
            run++;
        }

        return run;
    }

    /**
     * Rank a start candidate. Compared field by field, lower is better:
     *
     * 1. the longest correct digit chain led by the start;
     * 2. the earliest starting digit — an equal-length chain reads better from the front
     *    of the sequence, so a start on digit 1 beats one on digit 2;
     * 3. the most digits touched — a path that runs into a wrong digit tells the player
     *    more than one that reaches nothing at all;
     * 4. a closed loop over one that gets stuck;
     * 5. the shortest path before that loop/stuck point.
     *
     * (2) and (3) together keep the marker on the digit the player is building from,
     * rather than letting it drift to a tidier but less informative candidate.
     */
    private candidateRank(pos: Position, path: BlockPath): number[] {
        return [
            -this.getChainFromStart(path),
            this.grid.getDigitAt(pos) ?? this.grid.maxDigit + 1,
            -path.digitsHit.length,
            path.loopClosed ? 0 : 1,
            path.positions.length,
        ];
    }

    private static isBetterRank(rank: number[], best: number[]): boolean {
        for (let i = 0; i < rank.length; i++) {
            if (rank[i] !== best[i]) return rank[i] < best[i];
        }
        return false;
    }

    // Find the best starting candidate (see candidateRank for the ordering)
    private findBestCandidate(): { pos: Position, dir: Direction, path: BlockPath } | null {
        let best: { pos: Position, dir: Direction, path: BlockPath, rank: number[] } | null = null;

        for (const { pos, dir } of this.getStartCandidates()) {
            const path = this.tracePath(pos, dir);
            const rank = this.candidateRank(pos, path);
            if (!best || WallsGame.isBetterRank(rank, best.rank)) {
                best = { pos, dir, path, rank };
            }
        }

        return best ? { pos: best.pos, dir: best.dir, path: best.path } : null;
    }

    // Get the edge that would be crossed when moving from pos in direction dir
    private getEdgeCrossed(pos: Position, dir: Direction): Edge | null {
        // At grid boundary, wrapping occurs - no internal edge to cross
        if (dir === 'N' && pos.row === 0) return null;
        if (dir === 'S' && pos.row === this.grid.size - 1) return null;
        if (dir === 'W' && pos.col === 0) return null;
        if (dir === 'E' && pos.col === this.grid.size - 1) return null;

        return this.grid.getEdgeFromPosition(pos, dir);
    }

    // Wrap outside position back into grid
    private wrapPosition(outside: Outside): Position {
        const size = this.grid.size;
        return {
            row: ((outside.row % size) + size) % size,
            col: ((outside.col % size) + size) % size
        };
    }

    /**
     * Hook called when the block enters a new cell during path tracing.
     * Override in subclasses to add teleportation behavior.
     */
    protected onBlockEntersCell(
        cell: Position, _justTeleported: boolean
    ): { cell: Position; teleported: boolean } {
        return { cell, teleported: false };
    }

    // Trace the block's path starting from a position and direction
    tracePath(startPos: Position, startDir: Direction): BlockPath {
        const positions: Position[] = [startPos];
        const digitsHit: number[] = [];
        const digitsCrossedTwice: number[] = [];
        const teleportIndices: number[] = [];
        const wallsHit = new Set<string>();
        const visited = new Set<string>();
        // Directions the block has travelled through each digit. Re-crossing a
        // digit the same way is just the block retracing its own lap (benign);
        // only a crossing along a *different* direction is a real second pass.
        const digitCrossings = new Map<number, Set<Direction>>();

        let pos = startPos;
        let dir = startDir;
        let justTeleported = false;
        const maxIterations = this.grid.size * this.grid.size * 4;

        // Check starting position for digit
        const startDigit = this.grid.getDigitAt(pos);
        if (startDigit !== null) {
            digitsHit.push(startDigit);
            digitCrossings.set(startDigit, new Set([startDir]));
        }

        for (let i = 0; i < maxIterations; i++) {
            // Check for wall on the edge we're about to cross
            const edge = this.getEdgeCrossed(pos, dir);
            if (edge) {
                const wallDir = this.getWallAt(edge);
                if (wallDir !== null) {
                    wallsHit.add(edgeKey(edge));
                    dir = wallDir;
                }
            }

            // Move in current direction (with wrapping)
            const moveResult = this.grid.move(pos, dir);
            const nextPos = isInside(moveResult) ? moveResult : this.wrapPosition(moveResult);

            // Returning to the start cell only ends the trace when the block also
            // leaves it in the starting direction: that repeats the start state, so
            // the lap is closed. Arriving in any other direction is the block merely
            // crossing its own start cell, and the path carries on — the (cell,
            // direction) check below is what catches a genuine premature loop.
            if (nextPos.row === startPos.row && nextPos.col === startPos.col) {
                // Check what direction we'd have after wall redirect at start
                let finalDir = dir;
                const edgeAtStart = this.getEdgeCrossed(nextPos, dir);
                if (edgeAtStart) {
                    const wallDir = this.getWallAt(edgeAtStart);
                    if (wallDir !== null) {
                        wallsHit.add(edgeKey(edgeAtStart));
                        finalDir = wallDir;
                    }
                }

                if (finalDir === startDir) {
                    return {
                        positions,
                        digitsHit,
                        digitsCrossedTwice,
                        loopClosed: true,
                        stuck: false,
                        stuckPosition: null,
                        endDirection: finalDir,
                        teleportIndices,
                        wallsHit
                    };
                }
            }

            positions.push(nextPos);
            pos = nextPos;

            // Teleportation hook (vortex gates)
            const teleportResult = this.onBlockEntersCell(pos, justTeleported);
            if (teleportResult.teleported) {
                pos = teleportResult.cell;
                teleportIndices.push(positions.length);
                positions.push(pos);
                justTeleported = true;
            } else {
                justTeleported = false;
            }

            // State key for loop detection
            const stateKey = `${positionKey(pos)}:${dir}`;

            // Check for infinite loop (not returning to start)
            if (visited.has(stateKey)) {
                // Remove the duplicate position (already visited earlier)
                positions.pop();
                return {
                    positions,
                    digitsHit,
                    digitsCrossedTwice,
                    loopClosed: false,
                    stuck: true,
                    stuckPosition: positions[positions.length - 1],
                    endDirection: dir,
                    teleportIndices,
                    wallsHit
                };
            }
            visited.add(stateKey);

            // Check for digit
            const digit = this.grid.getDigitAt(pos);
            if (digit !== null) {
                const crossings = digitCrossings.get(digit);
                if (crossings === undefined) {
                    digitsHit.push(digit);
                    digitCrossings.set(digit, new Set([dir]));
                } else if (!crossings.has(dir)) {
                    crossings.add(dir);
                    if (!digitsCrossedTwice.includes(digit)) {
                        digitsCrossedTwice.push(digit);
                    }
                }
            }
        }

        return {
            positions,
            digitsHit,
            digitsCrossedTwice,
            loopClosed: false,
            stuck: true,
            stuckPosition: null,
            endDirection: dir,
            teleportIndices,
            wallsHit
        };
    }

    // Get the current path using auto-selected best starting candidate
    getPath(): BlockPath | null {
        if (this.cachedPath) return this.cachedPath;

        // No path if there are invalid walls (walls touching each other)
        const invalidWalls = this.getInvalidWalls();
        if (invalidWalls.size > 0) {
            this.startPosition = null;
            this.startDirection = null;
            return null;
        }

        const best = this.findBestCandidate();
        if (!best) {
            this.startPosition = null;
            this.startDirection = null;
            this.startOverride = null;
            return null;
        }

        // The auto-selection always owns the start cell. A player-chosen direction only
        // survives while the start stays put — once a better candidate moves it to
        // another digit, the override is dropped rather than dragged along.
        let { dir, path } = best;
        if (this.startOverride) {
            if (positionsEqual(this.startOverride.pos, best.pos)) {
                dir = this.startOverride.dir;
                path = this.tracePath(best.pos, dir);
            } else {
                this.startOverride = null;
            }
        }

        this.startPosition = best.pos;
        this.startDirection = dir;
        this.cachedPath = path;

        return this.cachedPath;
    }

    /**
     * Whether the path closes a clean loop through every digit in cyclic order —
     * the win condition apart from stray walls. Kept separate from isWinningPath so
     * the status bar can tell "solved but for unused walls" from "not solved yet".
     */
    private pathClosesDigitLoop(path: BlockPath): boolean {
        if (path.digitsCrossedTwice.length > 0) return false;
        if (!path.loopClosed) return false;
        if (path.digitsHit.length !== this.grid.digitCount) return false;

        // Check cyclic sequential order (can start at any digit)
        // e.g., [8, 9, 1, 2, 3, 4, 5, 6, 7] is valid
        for (let i = 1; i < this.grid.digitCount; i++) {
            const prev = path.digitsHit[i - 1];
            const curr = path.digitsHit[i];
            const expected = this.grid.nextDigit(prev);
            if (curr !== expected) return false;
        }

        return true;
    }

    /** Walls the block never redirects off in the given path — stray pieces that
     *  contribute nothing and, like Branches' dangling branches, block the win. */
    getUnusedWalls(path: BlockPath = this.getPath() ?? this.emptyPath()): string[] {
        const unused: string[] = [];
        for (const key of this.walls.keys()) {
            if (!path.wallsHit.has(key)) unused.push(key);
        }
        return unused;
    }

    private emptyPath(): BlockPath {
        return {
            positions: [], digitsHit: [], digitsCrossedTwice: [], loopClosed: false,
            stuck: false, stuckPosition: null, endDirection: null, wallsHit: new Set()
        };
    }

    /** Whether a traced path solves the puzzle, independent of which path is on show.
     *  Every placed wall must be used: a stray wall the block never touches blocks
     *  the win even when the digit loop is otherwise complete. */
    private isWinningPath(path: BlockPath): boolean {
        if (!this.pathClosesDigitLoop(path)) return false;
        return this.getUnusedWalls(path).length === 0;
    }

    checkWin(): boolean {
        const path = this.getPath();
        return path !== null && this.isWinningPath(path);
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const path = this.getPath();
        if (!path) return null;

        const wallCount = this.walls.size;

        // Count empty cells visited (cells without digits)
        const emptyCells = new Set<string>();
        for (const pos of path.positions) {
            if (!this.grid.hasDigitAt(pos)) {
                emptyCells.add(positionKey(pos));
            }
        }

        return calculateScore(wallCount, emptyCells.size);
    }

    /** Scoring-rules overlay: block-visited empty cells (secondary). Walls (primary)
     *  are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const secondaryCells: Position[] = [];
        const path = this.getPath();
        if (path) {
            const seen = new Set<string>();
            for (const pos of path.positions) {
                const key = positionKey(pos);
                if (this.grid.hasDigitAt(pos) || seen.has(key)) continue;
                seen.add(key);
                secondaryCells.push({ ...pos });
            }
        }
        return { secondaryCells };
    }

    /**
     * The longest cyclically consecutive run inside the digits the block hits —
     * the "correct chain". Shared by the status messages and the renderer, so the
     * digits painted teal are exactly the ones counted here.
     */
    getBestDigitRun(digitsHit: number[]): { start: number; length: number } {
        if (digitsHit.length === 0) return { start: 0, length: 0 };

        let bestStart = 0;
        let bestLength = 1;
        let currentStart = 0;
        let currentLength = 1;

        for (let i = 1; i < digitsHit.length; i++) {
            if (digitsHit[i] === this.grid.nextDigit(digitsHit[i - 1])) {
                currentLength++;
                if (currentLength > bestLength) {
                    bestLength = currentLength;
                    bestStart = currentStart;
                }
            } else {
                currentStart = i;
                currentLength = 1;
            }
        }

        return { start: bestStart, length: bestLength };
    }

    // Find the next digit needed after the longest correct sequence
    private getNextDigitNeeded(path: BlockPath): number | null {
        if (path.digitsHit.length === 0) return this.grid.minDigit;

        const { start, length } = this.getBestDigitRun(path.digitsHit);

        // If we have all digits in sequence, return null (loop closure needed)
        if (length >= this.grid.digitCount) return null;

        // Next digit is one after the last in the best run
        return this.grid.nextDigit(path.digitsHit[start + length - 1]);
    }

    /**
     * The first digit the block reaches out of sequence: the one it hits straight
     * after the correct chain, when that isn't the digit the chain needs next.
     * Null when every digit the block hits follows on cleanly.
     */
    getOutOfOrderDigit(): { reached: number; after: number; expected: number } | null {
        const path = this.getPath();
        if (!path) return null;

        const hits = path.digitsHit;
        const { start, length } = this.getBestDigitRun(hits);
        const next = start + length;
        // Nothing after the chain, or the chain starts part-way in (the block
        // stumbled into it, so there is no digit before the break to name).
        if (length === 0 || next >= hits.length || start > 0) return null;

        const after = hits[next - 1];
        return { reached: hits[next], after, expected: this.grid.nextDigit(after) };
    }

    getStatus(): string {
        const digitRange = `${this.grid.minDigit}-${this.grid.maxDigit}`;
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;
        const boldAmber = (text: string) =>
            `<span style="color:#fbbf24;font-weight:bold">${text}</span>`;

        if (this.walls.size === 0) {
            return `Place arrow walls to guide the block through digits ${digitRange}.`;
        }

        // Check for invalid walls first
        if (this.hasInvalidWalls()) {
            return `${boldRed('Invalid')}: some walls are touching.`;
        }

        const path = this.getPath();
        if (!path) return `Place arrow walls to guide the block through digits ${digitRange}.`;

        if (path.digitsCrossedTwice.length > 0) {
            // Edge case: a full correct chain that returns through the start digit in
            // the wrong direction. The player was just told to close the loop, so the
            // generic "crossed twice" warning misleads — name the real problem.
            const startDigit = path.digitsHit[0];
            const fullChain = this.getBestDigitRun(path.digitsHit).length >= this.grid.digitCount;
            if (fullChain && startDigit !== undefined && path.digitsCrossedTwice.includes(startDigit)) {
                return `${boldRed('Warning')}: the loop is closed in the wrong direction.`;
            }
            const digits = path.digitsCrossedTwice.sort((a, b) => a - b).join(', ');
            return `${boldRed('Warning')}: path crosses digit${path.digitsCrossedTwice.length > 1 ? 's' : ''} ${digits} more than once!`;
        }

        if (this.checkWin()) {
            return `You win! The block visits all digits ${digitRange} in order and loops back!`;
        }

        // The digit loop is complete, but stray walls the block never touches keep it
        // from being a win — flag them (they render amber) so the player clears them.
        if (this.pathClosesDigitLoop(path)) {
            const unused = this.getUnusedWalls(path).length;
            return `${boldAmber('Warning')}: ${unused} unused wall${unused > 1 ? 's' : ''} — remove ${unused > 1 ? 'them' : 'it'} to close the puzzle.`;
        }

        const outOfOrder = this.getOutOfOrderDigit();
        if (outOfOrder) {
            const { reached, after, expected } = outOfOrder;
            return `${boldRed('Warning')}: the block reaches digit ${reached} after digit ${after}. ` +
                `Digit ${expected} should come first.`;
        }

        const nextDigit = this.getNextDigitNeeded(path);

        if (nextDigit !== null) {
            return `Place walls to hit next digit ${nextDigit}`;
        }

        // All digits correct but loop not closed. Name the digit the block must
        // return to (the start digit) and remind that the direction must match.
        const startDigit = path.digitsHit[0];
        return `Place walls to reach digit ${startDigit} again, matching the starting direction`;
    }

    isStarted(): boolean {
        return this.walls.size > 0;
    }

    // Export walls in readable format: "A3-A4>N, B5-C5>S"
    exportWalls(): string {
        const parts: string[] = [];
        for (const [key, dir] of this.walls) {
            const edge = this.grid.deserializeEdge(key);
            if (!edge) continue;
            // Use cells adjacent to edge for readable format
            const cells = edgeAdjacentCells(edge);
            if (cells.length === 2) {
                const [a, b] = cells;
                parts.push(`${this.gs.serializePosition(a)}-${this.gs.serializePosition(b)}>${dir}`);
            }
        }
        return parts.join(', ');
    }

    // Export digit positions in format: "1:A3, 2:B5, ..."
    exportDigits(): string {
        const parts: string[] = [];
        for (let d = this.grid.minDigit; d <= this.grid.maxDigit; d++) {
            const pos = this.grid.getDigitPosition(d);
            if (pos) {
                parts.push(`${d}:${this.gs.serializePosition(pos)}`);
            }
        }
        return parts.join(', ');
    }

    /**
     * Serialize game state to string format.
     * Format: "A0A1E,B2B3N" - comma-separated edge positions with directions
     */
    serialize(): string {
        const entries: string[] = [];
        for (const [key, dir] of this.walls) {
            const edge = this.grid.deserializeEdge(key);
            if (!edge) continue;
            const cells = edgeAdjacentCells(edge);
            if (cells.length === 2) {
                const [a, b] = cells;
                entries.push(`${this.gs.serializePosition(a)}${this.gs.serializePosition(b)}${dir}`);
            }
        }
        return entries.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 5)) {
            // Format: A0A1E (two positions + direction)
            const match = entry.match(/^([A-J])(\d)([A-J])(\d)([NSEW])$/);
            if (!match) continue;

            const col1 = match[1].charCodeAt(0) - 'A'.charCodeAt(0);
            const row1 = parseInt(match[2], 10);
            const col2 = match[3].charCodeAt(0) - 'A'.charCodeAt(0);
            const row2 = parseInt(match[4], 10);
            const dir = match[5] as Direction;

            const pos1: Position = { row: row1, col: col1 };
            const pos2: Position = { row: row2, col: col2 };

            // Create edge from the two adjacent cells
            const edge = this.grid.createEdgeFromCells(pos1, pos2);
            if (edge && this.canPlaceWall(edge)) {
                this.walls.set(edgeKey(edge), dir);
            }
        }

        this.cachedPath = null;
    }
}
